// ─────────────────────────────────────────────────────────────────────────────
// Persona Strength — deterministic 0–100 richness rating for persona cards.
//
// Replaces the old ten-field yes/no completeness check (which read 100% for
// nearly every built persona). The score grades DEPTH, not just presence:
// number/richness of structured psychology entries, customer-voice quotes,
// completed research topics with findings, generated characters, and card
// imagery. The same computation drives the card gauge, the "Persona Ready"
// badge, and the grid's default Strength sort, so they can never disagree.
//
// Research/character counts don't live on the persona row — the persona list
// endpoint attaches them (characterCount, researchTopicCount,
// researchTopicsWithFindings) via grouped count queries. When absent (older
// payloads / other surfaces) those dimensions simply score 0.
// ─────────────────────────────────────────────────────────────────────────────

export interface PersonaStrengthInput {
  name?: string | null;
  awarenessStage?: string | null;
  summary?: string | null;
  worldview?: string | null;
  primaryPain?: string | null;
  desiredOutcome?: string | null;
  deepFear?: string | null;
  coreBeliefsJson?: unknown;
  objectionsJson?: unknown;
  trustTriggersJson?: unknown;
  purchaseDriversJson?: unknown;
  emotionalHooksJson?: unknown;
  languagePatternsJson?: unknown;
  customerVoiceQuotesJson?: unknown;
  cardQuote?: string | null;
  imageUrl?: string | null;
  heroImageUrl?: string | null;
  sketchImageUrl?: string | null;
  // Server-attached counts (persona list endpoint). Optional everywhere else.
  characterCount?: number | null;
  researchTopicCount?: number | null;
  researchTopicsWithFindings?: number | null;
}

/** Score at or above which a persona is considered "ready" (badge + copy). */
export const PERSONA_STRENGTH_READY_THRESHOLD = 70;

export function isPersonaStrengthReady(score: number): boolean {
  return score >= PERSONA_STRENGTH_READY_THRESHOLD;
}

/** True when the entry carries real content (non-blank string, or an object
 *  with at least one non-blank string value). */
function isMeaningfulEntry(entry: unknown): boolean {
  if (entry == null) return false;
  if (typeof entry === "string") return entry.trim().length > 0;
  if (typeof entry === "number" || typeof entry === "boolean") return true;
  if (Array.isArray(entry)) return entry.some(isMeaningfulEntry);
  if (typeof entry === "object") {
    return Object.values(entry as Record<string, unknown>).some(isMeaningfulEntry);
  }
  return false;
}

/** Count of meaningful items in a jsonb list field. Non-array truthy values
 *  (legacy single-string/object rows) count as one item. */
export function meaningfulItemCount(value: unknown): number {
  if (value == null) return 0;
  if (Array.isArray(value)) return value.filter(isMeaningfulEntry).length;
  return isMeaningfulEntry(value) ? 1 : 0;
}

/** Graded credit for a free-text field: 0 empty, half for a stub (<40 chars),
 *  full for developed text. */
function textScore(value: string | null | undefined, weight: number): number {
  const len = (value ?? "").trim().length;
  if (len === 0) return 0;
  return len >= 40 ? weight : weight / 2;
}

/** Depth-graded credit for a list field: linear up to `target` items. */
function listScore(value: unknown, target: number, weight: number): number {
  const n = meaningfulItemCount(value);
  return Math.min(n / target, 1) * weight;
}

function countScore(count: number | null | undefined, target: number, weight: number): number {
  const n = Math.max(0, Number(count ?? 0) || 0);
  return Math.min(n / target, 1) * weight;
}

/**
 * Deterministic 0–100 persona strength.
 *
 * Weight budget (sums to 100):
 *  - Core narrative (25): identity 3, summary 4, worldview 4, primaryPain 5,
 *    desiredOutcome 5, deepFear 4 — text fields graded by developed length.
 *  - Structured psychology (30): coreBeliefs 6/4·items, objections 6/4,
 *    trustTriggers 5/4, purchaseDrivers 5/3, emotionalHooks 4/3,
 *    languagePatterns 4/5 — linear credit up to the per-field item target.
 *  - Customer voice (10): quotes 9 (full at 3+), card quote 1.
 *  - Research (15): 3 for having topics + 12 · done-with-findings ratio
 *    (denominator floored at 3 so a single finished topic isn't "complete").
 *  - Characters (10): full credit at 3+ generated characters.
 *  - Imagery (10): profile photo 3, landscape hero 4, quote-strip sketch 3.
 */
export function computePersonaStrength(p: PersonaStrengthInput): number {
  let score = 0;

  // Core narrative — 25
  if ((p.name ?? "").trim() && (p.awarenessStage ?? "").trim()) score += 3;
  score += textScore(p.summary, 4);
  score += textScore(p.worldview, 4);
  score += textScore(p.primaryPain, 5);
  score += textScore(p.desiredOutcome, 5);
  score += textScore(p.deepFear, 4);

  // Structured psychology depth — 30
  score += listScore(p.coreBeliefsJson, 4, 6);
  score += listScore(p.objectionsJson, 4, 6);
  score += listScore(p.trustTriggersJson, 4, 5);
  score += listScore(p.purchaseDriversJson, 3, 5);
  score += listScore(p.emotionalHooksJson, 3, 4);
  score += listScore(p.languagePatternsJson, 5, 4);

  // Customer voice — 10
  score += listScore(p.customerVoiceQuotesJson, 3, 9);
  if ((p.cardQuote ?? "").trim()) score += 1;

  // Research — 15
  const topics = Math.max(0, Number(p.researchTopicCount ?? 0) || 0);
  const withFindings = Math.max(0, Number(p.researchTopicsWithFindings ?? 0) || 0);
  if (topics > 0) score += 3;
  score += Math.min(withFindings / Math.max(topics, 3), 1) * 12;

  // Characters — 10
  score += countScore(p.characterCount, 3, 10);

  // Imagery — 10
  if ((p.imageUrl ?? "").trim()) score += 3;
  if ((p.heroImageUrl ?? "").trim()) score += 4;
  if ((p.sketchImageUrl ?? "").trim()) score += 3;

  return Math.max(0, Math.min(100, Math.round(score)));
}
